import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getExtAdminAgents,
  getExtAgentDetail,
  approveExtAgent,
  assignExtAgentSectorRef,
  createExtAgent,
  getExtSectors,
  createExtAdminSector,
  updateExtAdminSector,
  deleteExtAdminSector,
} from "../../../redux/actions/profile";
import Flash from "../../../utility/Flash";
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer
} from "recharts";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";
const PAGE_SIZE = 20;

function Badge({ approved }) {
  return (
    <span style={{
      background: approved ? "rgba(167,204,72,0.15)" : "rgba(244,162,97,0.15)",
      color: approved ? "#5a8a00" : "#c0630a",
      borderRadius: 20, padding: "2px 12px", fontSize: 11, fontWeight: 600
    }}>
      {approved ? "Approved" : "Pending"}
    </span>
  );
}

function LocationMap({ address }) {
  if (!address || address.trim() === "") {
    return (
      <div style={styles.mapPlaceholder}>
        <i className="bi bi-geo-alt-slash" style={{ fontSize: 28, color: "#ccc" }} />
        <div style={{ color: "#bbb", fontSize: 12, marginTop: 6 }}>No location available</div>
      </div>
    );
  }
  return (
    <iframe
      title="Agent Location"
      src={"https://maps.google.com/maps?q=" + encodeURIComponent(address) + "&output=embed"}
      width="100%" height="200"
      style={{ border: 0, borderRadius: 8 }}
      allowFullScreen=""
      loading="lazy"
    />
  );
}

function AgentDetailModal({ detailData, onClose, onApprove, onAssignSector, sectors, approvingId }) {
  const agent = detailData?.agent;
  const [sectorId, setSectorId] = useState("");
  const [assigningId, setAssigningId] = useState(false);

  useEffect(() => {
    if (agent) setSectorId(agent.sectorRef?._id || agent.sectorRef || "");
  }, [agent]);

  if (!detailData) return null;
  const { merchants = [], totalEarnings = 0, feedbacks = [], monthlyReferrals = [] } = detailData;

  const handleSectorSave = async () => {
    setAssigningId(true);
    await onAssignSector(agent._id, sectorId || null);
    setAssigningId(false);
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div>
            <div style={styles.modalTitle}>{agent.fullName || agent.email}</div>
            <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>Agent Details</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {!agent.approve ? (
              <button style={styles.btnApprove} disabled={approvingId === agent._id} onClick={() => onApprove(agent._id, true)}>
                {approvingId === agent._id ? "…" : <><i className="bi bi-check-lg" /> Approve</>}
              </button>
            ) : (
              <button style={styles.btnDecline} disabled={approvingId === agent._id} onClick={() => onApprove(agent._id, false)}>
                {approvingId === agent._id ? "…" : <><i className="bi bi-x-lg" /> Revoke</>}
              </button>
            )}
            <button style={styles.closeBtn} onClick={onClose}><i className="bi bi-x-lg" /></button>
          </div>
        </div>
        <div style={styles.modalBody}>
          <div style={styles.commissionBanner}>
            <i className="bi bi-cash-coin" style={{ fontSize: 20, color: PRIMARY }} />
            <div>
              <div style={{ fontSize: 11, color: "#aaa", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Total Commission Earned</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: totalEarnings > 0 ? "#5a8a00" : BG }}>
                ₦{Number(totalEarnings).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
          <div style={styles.infoGrid}>
            <InfoItem label="Email" value={agent.email} />
            <InfoItem label="Mobile" value={agent.mobile || "—"} />
            <InfoItem label="Code" value={<code style={styles.codeInline}>{agent.agentCode}</code>} />
            <InfoItem label="Sector" value={agent.sector ? "Sector " + agent.sector : "—"} />
            <InfoItem label="Status" value={<Badge approved={agent.approve} />} />
            <InfoItem label="Last Login" value={agent.lastLogin ? new Date(agent.lastLogin).toLocaleString() : "Never"} />
            <InfoItem label="Joined" value={agent.createdAt ? new Date(agent.createdAt).toLocaleDateString() : "—"} />
            <div style={{ ...styles.infoItem, gridColumn: "1 / -1" }}>
              <span style={styles.infoLabel}>Address</span>
              <span style={styles.infoValue}>{agent.address || "—"}</span>
            </div>
          </div>

          {sectors && sectors.length > 0 && (
            <div style={{ ...styles.modalSection, background: "#f9f9f9", borderRadius: 10, padding: "14px 16px" }}>
              <div style={styles.modalSectionTitle}><i className="bi bi-map-fill" style={{ color: PRIMARY, marginRight: 6 }} />Assign Sector</div>
              <div style={{ display: "flex", gap: 8 }}>
                <select style={styles.sectorSelect} value={sectorId} onChange={(e) => setSectorId(e.target.value)}>
                  <option value="">— Unassigned —</option>
                  {sectors.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                </select>
                <button style={styles.btnSmall} onClick={handleSectorSave} disabled={assigningId}>
                  {assigningId ? "…" : "Save"}
                </button>
              </div>
              {sectorId && sectors.find((s) => s._id === sectorId)?.locations?.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8 }}>
                  {sectors.find((s) => s._id === sectorId).locations.map((loc, i) => (
                    <span key={i} style={{ fontSize: 11, background: "rgba(167,204,72,0.1)", border: "1px solid rgba(167,204,72,0.25)", color: "#4a6a00", borderRadius: 20, padding: "2px 8px" }}>
                      <i className="bi bi-geo-alt" style={{ fontSize: 9, marginRight: 2 }} />{loc}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={styles.modalSection}>
            <div style={styles.modalSectionTitle}><i className="bi bi-geo-alt-fill" style={{ color: PRIMARY, marginRight: 6 }} />Last Known Location</div>
            <LocationMap address={agent.address || ""} />
          </div>

          {monthlyReferrals.length > 0 && (
            <div style={styles.modalSection}>
              <div style={styles.modalSectionTitle}><i className="bi bi-graph-up" style={{ color: PRIMARY, marginRight: 6 }} />Monthly Merchant Referrals</div>
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={monthlyReferrals}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fill: "#888", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#888", fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="referrals" stroke={PRIMARY} strokeWidth={2} dot={{ fill: PRIMARY, r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div style={styles.modalSection}>
            <div style={styles.modalSectionTitle}><i className="bi bi-shop" style={{ color: PRIMARY, marginRight: 6 }} />Referred Merchants ({merchants.length})</div>
            {merchants.length === 0 ? (
              <div style={styles.emptySmall}>No merchants referred yet.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={styles.miniTable}>
                  <thead><tr>{["Store", "Email", "Plan", "Last Login"].map((h) => <th key={h} style={styles.miniTh}>{h}</th>)}</tr></thead>
                  <tbody>
                    {merchants.map((m, i) => {
                      const active = m.subscriptionEnd && Date.now() < m.subscriptionEnd;
                      return (
                        <tr key={i} style={styles.miniTr}>
                          <td style={styles.miniTd}>{m.storeInfo?.storeName || "—"}</td>
                          <td style={styles.miniTd}>{m.email}</td>
                          <td style={styles.miniTd}>
                            <span style={{ background: active ? "rgba(167,204,72,0.12)" : "#f5f5f5", color: active ? "#5a8a00" : "#888", borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 600 }}>{m.plan || "Free"}</span>
                          </td>
                          <td style={styles.miniTd}>{m.lastLogin ? new Date(m.lastLogin).toLocaleDateString() : "Never"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {feedbacks.length > 0 && (
            <div style={styles.modalSection}>
              <div style={styles.modalSectionTitle}><i className="bi bi-chat-left-text" style={{ color: PRIMARY, marginRight: 6 }} />Recent Feedbacks</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {feedbacks.slice(0, 3).map((f, i) => (
                  <div key={i} style={styles.feedbackCard}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: PRIMARY }}>Sector {f.sector}{f.location ? " · " + f.location : ""}</span>
                      <span style={{ fontSize: 11, color: "#aaa" }}>{f.date ? new Date(f.date).toLocaleDateString() : "—"}</span>
                    </div>
                    <p style={{ fontSize: 13, color: "#444", margin: 0 }}>{f.report}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SectorManager({ sectors, dispatch, createSector, updateSector, deleteSector, refresh }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", locations: [] });
  const [locInput, setLocInput] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => { setForm({ name: "", description: "", locations: [] }); setLocInput(""); setEditing(null); setShowForm(false); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, description: s.description || "", locations: [...(s.locations || [])] }); setLocInput(""); setShowForm(true); };
  const addLoc = () => { const l = locInput.trim(); if (l && !form.locations.includes(l)) setForm((f) => ({ ...f, locations: [...f.locations, l] })); setLocInput(""); };
  const removeLoc = (l) => setForm((f) => ({ ...f, locations: f.locations.filter((x) => x !== l) }));

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    const payload = { name: form.name.trim(), description: form.description, locations: form.locations };
    const result = editing ? await dispatch(updateSector(editing._id, payload)) : await dispatch(createSector(payload));
    setSaving(false);
    if (result.success) { refresh(); reset(); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this sector? Agents assigned to it will be unassigned.")) return;
    await dispatch(deleteSector(id));
    refresh();
  };

  return (
    <div style={styles.sectorMgrWrap}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: BG }}><i className="bi bi-map-fill" style={{ color: PRIMARY, marginRight: 6 }} />Sectors ({sectors.length})</div>
        <button style={styles.btnSmall} onClick={() => { reset(); setShowForm(true); }}>
          <i className="bi bi-plus-lg" /> New Sector
        </button>
      </div>

      {showForm && (
        <div style={{ background: "#fff", borderRadius: 8, padding: 16, marginBottom: 14, border: "1px solid #eee" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <input style={styles.input} placeholder="Sector name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input style={styles.input} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input style={{ ...styles.input, flex: 1 }} placeholder="Add location (press Enter)" value={locInput} onChange={(e) => setLocInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addLoc()} />
            <button style={styles.btnSmall} onClick={addLoc}><i className="bi bi-plus-lg" /></button>
          </div>
          {form.locations.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
              {form.locations.map((l) => (
                <span key={l} style={styles.locChip}>{l} <button style={styles.chipX} onClick={() => removeLoc(l)}>×</button></span>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button style={styles.btnSmall} onClick={handleSave} disabled={saving || !form.name.trim()}>{saving ? "…" : editing ? "Update" : "Create"}</button>
            <button style={{ ...styles.btnSmall, background: "#eee", color: "#666" }} onClick={reset}>Cancel</button>
          </div>
        </div>
      )}

      {sectors.length === 0 ? (
        <div style={{ color: "#bbb", fontSize: 13, textAlign: "center", padding: "16px 0" }}>No sectors yet. Create one to assign agents.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
          {sectors.map((s) => {
            const mapSrc = s.locations?.[0] ? `https://maps.google.com/maps?q=${encodeURIComponent(s.locations[0])}&output=embed` : null;
            return (
              <div key={s._id} style={styles.sectorCard}>
                {mapSrc && <iframe title={s.name} src={mapSrc} style={{ width: "100%", height: 100, border: "none", display: "block" }} frameBorder="0" />}
                <div style={{ padding: "10px 12px" }}>
                  <div style={{ fontWeight: 700, color: BG, fontSize: 13 }}>{s.name}</div>
                  {s.description && <div style={{ fontSize: 11, color: "#888", marginBottom: 6 }}>{s.description}</div>}
                  {s.locations?.length > 0 && (
                    <div style={{ fontSize: 11, color: "#aaa", marginBottom: 8 }}>
                      <i className="bi bi-geo-alt" style={{ marginRight: 2 }} />{s.locations.join(", ")}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 6 }}>
                    <button style={{ ...styles.btnSmall, fontSize: 11 }} onClick={() => openEdit(s)}>
                      <i className="bi bi-pencil-fill" /> Edit
                    </button>
                    <button style={{ ...styles.btnSmall, background: "rgba(230,57,70,0.1)", color: "#e63946", fontSize: 11 }} onClick={() => handleDelete(s._id)}>
                      <i className="bi bi-trash3" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InfoItem({ label, value, accent }) {
  return (
    <div style={styles.infoItem}>
      <span style={styles.infoLabel}>{label}</span>
      <span style={{ ...styles.infoValue, ...(accent ? { color: PRIMARY, fontWeight: 700 } : {}) }}>{value}</span>
    </div>
  );
}

export default function AgentManagement() {
  const dispatch = useDispatch();
  const agents = useSelector((s) => s.profile.extAdminAgents);
  const sectors = useSelector((s) => s.profile.extSectors || []);

  const [showCreate, setShowCreate] = useState(false);
  const [showSectorMgr, setShowSectorMgr] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", mobile: "", fullName: "", address: "", sector: "" });
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [sectorEdit, setSectorEdit] = useState(null);
  const [sectorValue, setSectorValue] = useState("");
  const [detailData, setDetailData] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    dispatch(getExtAdminAgents());
    dispatch(getExtSectors());
  }, [dispatch]);

  useEffect(() => { setPage(1); }, [search, filterStatus]);

  const allAgents = agents || [];
  const filtered = allAgents.filter((a) => {
    const q = search.toLowerCase();
    const matchSearch = !q || [a.fullName, a.email, a.mobile, a.agentCode].some((v) => v && v.toLowerCase().includes(q));
    const matchStatus = filterStatus === "all" || (filterStatus === "approved" ? a.approve : !a.approve);
    return matchSearch && matchStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const goTo = (p) => setPage(Math.min(Math.max(1, p), totalPages));

  const pageNumbers = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);
    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("...");
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.mobile || !form.fullName) {
      return Flash("error", "Name, email, mobile and password are required", "", 3000);
    }
    const result = await dispatch(createExtAgent(form));
    if (result && result.success) {
      Flash("success", "Agent created successfully", "", 3000);
      setShowCreate(false);
      setForm({ email: "", password: "", mobile: "", fullName: "", address: "", sector: "" });
      dispatch(getExtAdminAgents());
    } else {
      Flash("error", result?.message || "Failed to create agent", "", 4000);
    }
  };

  const handleApprove = async (id, state) => {
    setApprovingId(id);
    const result = await dispatch(approveExtAgent(id, state));
    setApprovingId(null);
    if (result && result.success) {
      Flash("success", state ? "Agent approved" : "Agent declined", "", 3000);
      dispatch(getExtAdminAgents());
    } else {
      Flash("error", result?.message || "Action failed", "", 3000);
    }
  };

  const handleSector = async (id) => {
    const selectedSector = sectors.find((s) => s._id === sectorValue);
    if (sectorValue && selectedSector) {
      const result = await dispatch(assignExtAgentSectorRef(id, sectorValue));
      if (result && result.success) {
        Flash("success", "Assigned to " + selectedSector.name, "", 3000);
        setSectorEdit(null);
        dispatch(getExtAdminAgents());
      } else {
        Flash("error", result?.message || "Failed to assign sector", "", 3000);
      }
    } else {
      Flash("error", "Please select a sector", "", 3000);
    }
  };

  const handleAssignSectorFromModal = async (agentId, sectorId) => {
    const result = await dispatch(assignExtAgentSectorRef(agentId, sectorId));
    if (result && result.success) {
      Flash("success", sectorId ? "Sector assigned" : "Sector removed", "", 3000);
      dispatch(getExtAdminAgents());
    } else {
      Flash("error", result?.message || "Failed to assign sector", "", 3000);
    }
  };

  const openDetail = async (id) => {
    const result = await dispatch(getExtAgentDetail(id));
    if (result && result.success) setDetailData(result);
  };

  return (
    <div>
      {detailData && (
        <AgentDetailModal
          detailData={detailData}
          onClose={() => setDetailData(null)}
          onApprove={handleApprove}
          onAssignSector={handleAssignSectorFromModal}
          sectors={sectors}
          approvingId={approvingId}
        />
      )}

      <div style={styles.header}>
        <div>
          <h2 style={styles.heading}>My Agents</h2>
          <div style={{ fontSize: 13, color: "#888" }}>{allAgents.length} agent{allAgents.length !== 1 ? "s" : ""} under your management</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={{ ...styles.btnPrimary, background: "#f0f0f0", color: BG }} onClick={() => setShowSectorMgr(!showSectorMgr)}>
            <i className="bi bi-map-fill" style={{ marginRight: 6 }} />
            {showSectorMgr ? "Close Sectors" : "Manage Sectors"}
          </button>
          <button style={styles.btnPrimary} onClick={() => setShowCreate(!showCreate)}>
            <i className="bi bi-person-plus-fill" style={{ marginRight: 6 }} />
            {showCreate ? "Cancel" : "Create Agent"}
          </button>
        </div>
      </div>

      {showSectorMgr && (
        <SectorManager
          sectors={sectors}
          dispatch={dispatch}
          createSector={createExtAdminSector}
          updateSector={updateExtAdminSector}
          deleteSector={deleteExtAdminSector}
          refresh={() => dispatch(getExtSectors())}
        />
      )}

      {showCreate && (
        <div style={styles.formCard}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: BG, marginBottom: 16 }}>New Agent</h3>
          <form onSubmit={handleCreate} style={styles.formGrid}>
            <input style={styles.input} placeholder="Full Name *" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
            <input style={styles.input} placeholder="Email *" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input style={styles.input} placeholder="Mobile *" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
            <input style={styles.input} placeholder="Password *" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <input style={styles.input} placeholder="Address (optional)" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            <select style={{ ...styles.input, background: "#fff" }} value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })}>
              <option value="">Select Sector (optional)</option>
              {sectors.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
            <button type="submit" style={{ ...styles.btnPrimary, alignSelf: "flex-end" }}>Create</button>
          </form>
        </div>
      )}

      <div style={styles.toolbar}>
        <div style={styles.searchBox}>
          <i className="bi bi-search" style={{ color: "#bbb", marginRight: 8 }} />
          <input style={styles.searchInput} placeholder="Search by name, email, code…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div style={styles.tabs}>
          {["all", "approved", "pending"].map((f) => (
            <button key={f} style={{ ...styles.tab, ...(filterStatus === f ? styles.tabActive : {}) }} onClick={() => setFilterStatus(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}&nbsp;
              <span style={{ fontSize: 10, opacity: 0.7 }}>({f === "all" ? allAgents.length : allAgents.filter((a) => f === "approved" ? a.approve : !a.approve).length})</span>
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={styles.empty}>
          <i className="bi bi-people" style={{ fontSize: 40, color: "#ccc" }} />
          <p style={{ marginTop: 12, color: "#888" }}>No agents found.</p>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={styles.table}>
            <thead>
              <tr>{["Agent", "Code", "Sector", "Status", "Merchants", "Commission", "Last Login", "Actions"].map((h) => <th key={h} style={styles.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {paginated.map((agent) => (
                <tr key={agent._id} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={{ fontWeight: 600, color: BG, fontSize: 13 }}>{agent.fullName || "—"}</div>
                    <div style={{ fontSize: 11, color: "#999" }}>{agent.email}</div>
                  </td>
                  <td style={styles.td}><code style={styles.codeTag}>{agent.agentCode}</code></td>
                  <td style={styles.td}>
                    {sectorEdit === agent._id ? (
                      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        <select style={{ ...styles.input, padding: "4px 6px", fontSize: 12, width: "auto" }} value={sectorValue} onChange={(e) => setSectorValue(e.target.value)}>
                          <option value="">— None —</option>
                          {sectors.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                        </select>
                        <button style={styles.btnSmall} onClick={() => handleSector(agent._id)}>Save</button>
                        <button style={{ ...styles.btnSmall, background: "#eee", color: "#666" }} onClick={() => setSectorEdit(null)}>✕</button>
                      </div>
                    ) : (
                      <span style={{ cursor: "pointer", color: PRIMARY, fontWeight: 600 }} onClick={() => { setSectorEdit(agent._id); setSectorValue(agent.sectorRef?._id || ""); }}>
                        {agent.sectorRef?.name || (agent.sector ? "Sector " + agent.sector : "—")} <i className="bi bi-pencil-fill" style={{ fontSize: 9 }} />
                      </span>
                    )}
                  </td>
                  <td style={styles.td}><Badge approved={agent.approve} /></td>
                  <td style={styles.td}><span style={{ fontWeight: 700, color: BG }}>{(agent.merchants || []).length}</span></td>
                  <td style={styles.td}>
                    <span style={{ fontWeight: 700, color: agent.totalEarnings > 0 ? "#5a8a00" : "#ccc" }}>
                      {agent.totalEarnings > 0 ? "₦" + Number(agent.totalEarnings).toLocaleString() : "—"}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontSize: 12, color: "#555" }}>{agent.lastLogin ? new Date(agent.lastLogin).toLocaleDateString() : "Never"}</div>
                    {agent.lastLogin && <div style={{ fontSize: 10, color: "#bbb" }}>{new Date(agent.lastLogin).toLocaleTimeString()}</div>}
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button style={styles.btnView} onClick={() => openDetail(agent._id)}>
                        <i className="bi bi-eye-fill" style={{ marginRight: 4 }} />View
                      </button>
                      {!agent.approve ? (
                        <button style={styles.btnApprove} onClick={() => handleApprove(agent._id, true)}>Approve</button>
                      ) : (
                        <button style={styles.btnDecline} onClick={() => handleApprove(agent._id, false)}>Decline</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div style={styles.pagination}>
          <span style={styles.pageInfo}>
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div style={styles.pageControls}>
            <button style={{ ...styles.pageBtn, ...(page === 1 ? styles.pageBtnDisabled : {}) }} onClick={() => goTo(page - 1)} disabled={page === 1}>
              <i className="bi bi-chevron-left" />
            </button>
            {pageNumbers().map((p, i) =>
              p === "..." ? (
                <span key={`e-${i}`} style={styles.ellipsis}>…</span>
              ) : (
                <button key={p} style={{ ...styles.pageBtn, ...(p === page ? styles.pageBtnActive : {}) }} onClick={() => goTo(p)}>{p}</button>
              )
            )}
            <button style={{ ...styles.pageBtn, ...(page === totalPages ? styles.pageBtnDisabled : {}) }} onClick={() => goTo(page + 1)} disabled={page === totalPages}>
              <i className="bi bi-chevron-right" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 20 },
  heading: { fontSize: 22, fontWeight: 700, color: BG, margin: 0 },
  btnPrimary: { background: PRIMARY, color: "#fff", border: "none", borderRadius: 7, padding: "9px 18px", cursor: "pointer", fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center" },
  btnSmall: { background: PRIMARY, color: "#fff", border: "none", borderRadius: 4, padding: "4px 10px", cursor: "pointer", fontSize: 12 },
  btnApprove: { background: "rgba(167,204,72,0.15)", color: "#5a8a00", border: "1px solid " + PRIMARY, borderRadius: 4, padding: "4px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  btnDecline: { background: "rgba(230,57,70,0.1)", color: "#e63946", border: "1px solid #e63946", borderRadius: 4, padding: "4px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  btnView: { background: "rgba(79,176,198,0.12)", color: "#2980b9", border: "1px solid #4fb0c6", borderRadius: 4, padding: "4px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center" },
  formCard: { background: "#fff", borderRadius: 10, padding: "20px 24px", boxShadow: "0 2px 8px rgba(0,0,0,0.07)", marginBottom: 24 },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12, alignItems: "end" },
  input: { border: "1.5px solid #e0e0e0", borderRadius: 7, padding: "9px 12px", fontSize: 14, outline: "none", width: "100%", boxSizing: "border-box" },
  toolbar: { display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" },
  searchBox: { display: "flex", alignItems: "center", background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "8px 14px", flex: 1, minWidth: 220 },
  searchInput: { border: "none", outline: "none", fontSize: 14, flex: 1, background: "transparent" },
  tabs: { display: "flex", gap: 6 },
  tab: { padding: "7px 16px", borderRadius: 20, border: "1.5px solid #e0e0e0", background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 500 },
  tabActive: { background: PRIMARY, color: "#fff", borderColor: PRIMARY },
  table: { width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 10, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" },
  th: { background: BG, color: "#fff", padding: "12px 14px", textAlign: "left", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" },
  tr: { borderBottom: "1px solid #f5f5f5" },
  td: { padding: "12px 14px", fontSize: 13, color: "#444", verticalAlign: "middle" },
  codeTag: { background: "#f0f0f0", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontFamily: "monospace" },
  empty: { textAlign: "center", padding: 60, background: "#fff", borderRadius: 10 },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16 },
  modal: { background: "#fff", borderRadius: 14, width: "100%", maxWidth: 720, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "20px 24px", borderBottom: "1px solid #f0f0f0", position: "sticky", top: 0, background: "#fff", zIndex: 1 },
  modalTitle: { fontSize: 18, fontWeight: 700, color: BG },
  closeBtn: { background: "#f5f5f5", border: "none", borderRadius: 8, width: 34, height: 34, cursor: "pointer", fontSize: 15, color: "#666", display: "flex", alignItems: "center", justifyContent: "center" },
  modalBody: { padding: "20px 24px" },
  modalSection: { marginBottom: 24 },
  modalSectionTitle: { fontSize: 13, fontWeight: 700, color: BG, marginBottom: 12, display: "flex", alignItems: "center" },
  infoGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "12px 20px", marginBottom: 24 },
  infoItem: { display: "flex", flexDirection: "column", gap: 4 },
  infoLabel: { fontSize: 10, fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: 0.5 },
  infoValue: { fontSize: 13, color: "#333" },
  codeInline: { background: "#f0f0f0", padding: "2px 8px", borderRadius: 4, fontSize: 12, fontFamily: "monospace", color: BG },
  mapPlaceholder: { height: 200, background: "#f5f5f5", borderRadius: 8, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" },
  miniTable: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  miniTh: { background: "#f5f5f5", padding: "8px 10px", textAlign: "left", color: "#666", fontWeight: 600, fontSize: 11 },
  miniTr: { borderBottom: "1px solid #f5f5f5" },
  miniTd: { padding: "8px 10px", color: "#555" },
  emptySmall: { color: "#aaa", fontSize: 13, padding: "12px 0" },
  feedbackCard: { background: "#f9f9f9", borderRadius: 8, padding: "12px 14px", borderLeft: "3px solid " + PRIMARY },
  sectorSelect: { flex: 1, border: "1.5px solid #e0e0e0", borderRadius: 7, padding: "7px 10px", fontSize: 13, outline: "none", background: "#fff" },
  sectorMgrWrap: { background: "#f5f5f5", borderRadius: 10, padding: "16px 20px", marginBottom: 24 },
  sectorCard: { background: "#fff", borderRadius: 8, overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.07)" },
  locChip: { display: "flex", alignItems: "center", gap: 4, background: "rgba(167,204,72,0.1)", border: "1px solid rgba(167,204,72,0.25)", color: "#4a6a00", borderRadius: 20, padding: "2px 10px", fontSize: 11 },
  chipX: { background: "none", border: "none", cursor: "pointer", color: "#aaa", fontSize: 13, padding: 0, lineHeight: 1 },
  commissionBanner: { display: "flex", alignItems: "center", gap: 14, background: "rgba(167,204,72,0.08)", border: "1.5px solid rgba(167,204,72,0.3)", borderRadius: 10, padding: "14px 18px", marginBottom: 20 },
  pagination: { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, flexWrap: "wrap", gap: 8 },
  pageInfo: { fontSize: 12, color: "#888" },
  pageControls: { display: "flex", alignItems: "center", gap: 4 },
  pageBtn: { minWidth: 32, height: 32, padding: "0 8px", border: "1.5px solid #e0e0e0", borderRadius: 6, background: "#fff", fontSize: 12, fontWeight: 600, color: "#555", cursor: "pointer" },
  pageBtnActive: { background: BG, borderColor: BG, color: "#fff" },
  pageBtnDisabled: { opacity: 0.4, cursor: "not-allowed" },
  ellipsis: { padding: "0 4px", color: "#bbb", fontSize: 13 },
};
